import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

// Assuming these imports are available in the project structure
import VideoList from '../../app/bento-components/video-list/video-list';
import { parseVideo, getPageCount } from '../../app/bento-components/video-list/video-utils';
import {
    CAROUSEL_ITEMS_PER_PAGE,
    PAGINATION_TYPES
} from '../../app/bento-components/video-list/metadata';
import VideoCTA from './video-list-cta';
import { getVideos } from './ajax';
import { ALL_SEASONS } from './constants';

const htmlTag = document.querySelectorAll('html')[0];
const SMALL_BREAKPOINT_PX = 767;

const isSmallBreakpoint = () => window.matchMedia(`(max-width: ${SMALL_BREAKPOINT_PX}px)`).matches;

/**
 * Return the settings adjusted for the active viewport. On small screens the
 * list is always rendered as a carousel, regardless of the configured
 * pagination type. This is the single place that maps "breakpoint -> settings".
 */
const getResponsiveSettings = (settings) => {
    if (isSmallBreakpoint()) {
        return {
            ...settings,
            paginationType: PAGINATION_TYPES.CAROUSEL,
            itemsPerPage: CAROUSEL_ITEMS_PER_PAGE
        };
    }
    return settings;
};

const VideoListHOCFE = (props) => {
    const { settings: initialSettings, API, data, id, columnSize, callsign, site } = props;

    // --- State Initialization ---
    // `localSettings` is the single source of truth for the request parameters
    // (pagination type, items per page, selected shows, order...). It is lazily
    // initialised for the current breakpoint so the very first fetch on mount
    // already uses the correct settings (no double fetch on mount).
    const [localSettings, setLocalSettings] = useState(() =>
        getResponsiveSettings(initialSettings)
    );
    const [videos, setVideos] = useState([]);
    const [pageCount, setPageCount] = useState(1);
    const [currentVideo, setCurrentVideo] = useState({});
    const [overlayVideo] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [resetSwiper, setResetSwiper] = useState(false);
    const [isLoadMoreEnabled, setIsLoadMoreEnabled] = useState(false);
    const [selectedSeasonFilter, setSelectedSeasonFilter] = useState(ALL_SEASONS);

    const { selectedShows } = localSettings;

    // Keep a ref of the latest settings so the (non-reactive) resize handler can
    // compare against the current values without being re-created on every change.
    const localSettingsRef = useRef(localSettings);
    useEffect(() => {
        localSettingsRef.current = localSettings;
    }, [localSettings]);

    // Identity of the last request actually issued: its serialised params (`key`)
    // and the season it targeted. Used by the fetch effect to (a) reset to page 1
    // when the season changes and (b) coalesce the extra render that a
    // season/page change produces into a single request. Keying on the issued
    // request (rather than on render order) keeps this correct regardless of how
    // React batches or flushes the intermediate renders.
    const lastRequestRef = useRef(null);

    // -------------------------------------------------------------------------
    // Single data-fetching effect.
    //
    // Every trigger that must issue a request — initial mount, page navigation,
    // season filter change and responsive settings change — flows through this
    // one effect, so `getVideos` is called from exactly one place with the same
    // parameters and the same response handling.
    //
    // It runs as a *layout* effect so a navigation/reset settles synchronously
    // within the same commit instead of being deferred, which keeps successive
    // interactions (e.g. paginate, then filter by season) from overlapping.
    // Duplicate requests are coalesced via `lastRequestRef` (see below).
    // -------------------------------------------------------------------------
    useLayoutEffect(() => {
        // Don't fetch if no shows are selected.
        if (!selectedShows || selectedShows.length === 0) {
            return undefined;
        }

        // A season change also resets the page to 1, but the two state updates
        // (season + page) are only batched into a single render inside React's
        // own event handlers. When triggered elsewhere, React renders the
        // intermediate state (new season, stale page) then the page-reset state,
        // which would fire two requests. Resolve this from the *issued request*
        // identity instead of render order:
        //   - When the requested season differs from the last issued one, force
        //     page 1 (the reset), regardless of the (possibly stale) currentPage.
        //   - Skip when the resulting request is identical to the last one issued
        //     (the redundant render), so exactly one request is made.
        const requestedSeason = (selectedSeasonFilter && selectedSeasonFilter.value) || '';
        const last = lastRequestRef.current;
        const seasonChanged = !last || last.season !== requestedSeason;
        const effectivePage = seasonChanged ? 1 : currentPage;

        const { paginationType, itemsPerPage } = localSettings;
        // Page 1 means a fresh load ("reset"); any other page appends
        // (LOAD_MORE / CAROUSEL) or replaces the visible page (PAGINATION).
        const isReset = effectivePage === 1;

        const params = { ...localSettings, page: effectivePage };
        if (requestedSeason) {
            // Apply the front-end selected season filter.
            params.seasonId = requestedSeason;
            params.selectedShows = []; // Clear shows when filtering by season.
        }

        // De-duplicate: an identical request would return identical data, so the
        // extra render produced by a season/page change is coalesced away here.
        const requestKey = JSON.stringify({
            page: effectivePage,
            seasonId: params.seasonId || '',
            selectedShows: params.selectedShows,
            itemsPerPage,
            paginationType,
            videoType: localSettings.videoType,
            order: localSettings.order
        });
        if (last && last.key === requestKey) {
            return undefined;
        }
        lastRequestRef.current = { key: requestKey, season: requestedSeason };

        const showLoading = () => {
            if (paginationType === PAGINATION_TYPES.CAROUSEL) {
                return;
            }
            if (paginationType === PAGINATION_TYPES.LOAD_MORE) {
                // Disable the Load More button while a request is in flight.
                setIsLoadMoreEnabled(false);
                if (!isReset) {
                    // Append placeholder items below the existing ones.
                    setVideos((prev) => [...prev, ...Array(itemsPerPage).fill({})]);
                    return;
                }
            }
            setVideos([]);
        };

        // Guard against races: if the dependencies change before this request
        // resolves, ignore its (now stale) response so a slower earlier request
        // can't overwrite newer data.
        let cancelled = false;

        getVideos(API, params, showLoading)
            .done((response) => {
                if (cancelled) {
                    return;
                }
                const parsedVideos = response.data.map((video) => parseVideo(video));
                setPageCount(getPageCount(response));

                if (paginationType === PAGINATION_TYPES.PAGINATION) {
                    setVideos(parsedVideos);
                    if (isReset) {
                        setCurrentVideo(parsedVideos[0]);
                    }
                    return;
                }

                // LOAD_MORE and CAROUSEL: replace on reset, otherwise append.
                if (isReset) {
                    setVideos(parsedVideos);
                    setCurrentVideo(parsedVideos[0]);
                } else {
                    // Drop the loading placeholders (empty objects with no `id`)
                    // that `showLoading` appended before adding the real videos.
                    setVideos((prev) => [
                        ...prev.filter((video) => video && video.id),
                        ...parsedVideos
                    ]);
                }

                if (paginationType === PAGINATION_TYPES.LOAD_MORE) {
                    setIsLoadMoreEnabled(true);
                }
            })
            .fail((xhr, status, err) => {
                if (cancelled) {
                    return;
                }
                console.error('[VideoList] Fetch failed:', status, err);
            });

        return () => {
            cancelled = true;
        };
    }, [API, localSettings, currentPage, selectedSeasonFilter, selectedShows]);

    // --- Responsive handling ---------------------------------------------------
    const resizeHandler = useCallback(() => {
        const responsive = getResponsiveSettings(initialSettings);
        const current = localSettingsRef.current;

        // Nothing to do if the responsive settings already match the current
        // ones — bail out so we don't trigger a redundant render/fetch.
        if (
            current.paginationType === responsive.paginationType &&
            current.itemsPerPage === responsive.itemsPerPage
        ) {
            return;
        }

        // These updates are batched into a single render, so the fetch effect
        // above runs exactly once.
        setResetSwiper(true);
        setLocalSettings((prevSettings) => ({
            ...prevSettings,
            paginationType: responsive.paginationType,
            itemsPerPage: responsive.itemsPerPage
        }));
        setCurrentPage(1);
    }, [initialSettings]);

    useEffect(() => {
        // `localSettings` was already initialised for the current breakpoint, so
        // the fetch effect handles the initial load. Here we only need to react
        // to subsequent viewport changes.
        window.addEventListener('resize', resizeHandler, false);
        return () => window.removeEventListener('resize', resizeHandler);
    }, [resizeHandler]);

    // --- Handlers --------------------------------------------------------------
    const setPage = (pageNumber) => {
        const pageCountValue = Math.max(1, pageCount);
        const validPage = Math.min(Math.max(1, pageNumber), pageCountValue);
        setCurrentPage(validPage);
    };

    const videoClick = useCallback(
        (e, video) => {
            e.preventDefault();
            setCurrentVideo(video);
            setShowVideo(true);
            if (overlayVideo) {
                htmlTag.classList.add('modal-open');
            }
        },
        [overlayVideo]
    );

    const videoClose = useCallback(() => {
        setShowVideo(false);
        htmlTag.classList.remove('modal-open');
    }, []);

    // Selecting a season only updates state; the single fetch effect reacts to
    // the change and coalesces the season + page-reset renders into one request
    // (see the fetch effect and `lastRequestRef`).
    const handleSelectSeasonFilter = useCallback((season) => {
        setResetSwiper(true);
        setSelectedSeasonFilter(season);
        setCurrentPage(1);
    }, []);

    const VideoListTitle = () => (
        <h1 className="video-list-title">{data.title || data.titlePlaceholder}</h1>
    );

    return (
        <div className="bento-component">
            <VideoList
                {...props}
                resetSwiper={resetSwiper}
                setResetSwiper={setResetSwiper}
                columnSize={columnSize}
                videos={videos}
                settings={localSettings}
                data={data}
                pageCount={pageCount}
                currentPage={currentPage}
                setCurrentPage={setPage}
                componentId={id}
                CallToAction={VideoCTA}
                currentVideo={currentVideo}
                videoClick={videoClick}
                videoClose={videoClose}
                VideoListTitle={VideoListTitle}
                showVideo={showVideo}
                playerClass={overlayVideo ? 'overlay' : ''}
                isLoadMoreEnabled={isLoadMoreEnabled}
                callsign={callsign}
                currentSite={{ isPillButtonAvailable: site.is_pill_button_available }}
                handleSelectSeasonFilter={handleSelectSeasonFilter}
                selectedSeasonFilter={selectedSeasonFilter}
            />
        </div>
    );
};

// --- Prop Types (Kept the original definition) ---
VideoListHOCFE.propTypes = {
    id: PropTypes.string.isRequired,
    columnSize: PropTypes.number.isRequired,
    linkWidget: PropTypes.node,
    data: PropTypes.object.isRequired,
    API: PropTypes.object.isRequired,
    settings: PropTypes.shape({
        selectedSeasonFilter: PropTypes.object,
        selectedShows: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                title: PropTypes.string.isRequired
            })
        ),
        selectedSeasons: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                value: PropTypes.string.isRequired
            })
        ),
        itemsPerPage: PropTypes.number.isRequired,
        displayVideoTitle: PropTypes.bool.isRequired,
        displayShowTitle: PropTypes.bool.isRequired,
        displayVideoDescription: PropTypes.bool.isRequired,
        displayVideoMeta: PropTypes.bool.isRequired,
        videoType: PropTypes.string.isRequired,
        paginationType: PropTypes.string.isRequired,
        order: PropTypes.shape({
            value: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired
        }),
        player: PropTypes.shape({
            autoplay: PropTypes.bool.isRequired,
            topbar: PropTypes.bool.isRequired
        }),
        selectedRegions: PropTypes.array
    }),
    callsign: PropTypes.string,
    site: PropTypes.shape({
        is_pill_button_available: PropTypes.bool.isRequired
    })
};

export default VideoListHOCFE;
